import { TimeInForceType } from '@/compiled_proto/com/celertech/positionmanager/api/enums/TimeInForceTypeProto';
import { accumulatePriceBookBidAsks, calculateTickerVWAP } from '@/helpers/orderbookHelper';
import { useAppSelector } from '@/state/hooks';
import { TraderMarket } from '@/state/reducers/traderMarketSlice';
import { RootState } from '@/state/store';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import { GetColorIndexesReturn, GetPipIndexesReturn, useInstrument } from '@/utils/hooks/useInstrument';
import usePreviousSelector from '@/utils/hooks/usePreviousSelector';
import BigNumber from 'bignumber.js';
import { useCallback, useMemo, useState } from 'react';
import usePreviousState from './usePreviousState';

interface PreviousBidAsk {
    bid: number;
    ask: number;
}

export interface UseTickerOrderBookReturn {
    bestBid: {
        price: number;
        pricePips?: GetPipIndexesReturn;
        highlights?: GetColorIndexesReturn;
    };
    bestAsk: {
        price: number;
        pricePips?: GetPipIndexesReturn;
        highlights?: GetColorIndexesReturn;
    };
    bidVWAP: {
        price: number;
        pricePips?: GetPipIndexesReturn;
        highlights?: GetColorIndexesReturn;
        isNoPrice?: boolean;
    };
    askVWAP: {
        price: number;
        pricePips?: GetPipIndexesReturn;
        highlights?: GetColorIndexesReturn;
        isNoPrice?: boolean;
    };
    spread?: string;
    vwapSpread?: string;
    settlementDate?: string;
}

const defaultBidAsk = { bid: 0, ask: 0 };

const useTickerOrderBook = (
    market: TraderMarket,
    quantity: number,
    duration: TimeInForceType,
    priceDecimals = 0
): UseTickerOrderBookReturn => {
    const selectBidAsk = useCallback(
        (state: RootState) => state.marketPair.bidAsk[market.celer] || defaultBidAsk,
        [market.celer]
    );

    const best = useAppSelector(selectBidAsk);
    const prevBest = usePreviousSelector(selectBidAsk) as PreviousBidAsk;
    const priceBooks = useAppSelector((state) => state.celerMarket.priceBooks[market.celer]);
    const { bids, asks } = useMemo(() => accumulatePriceBookBidAsks(priceBooks), [priceBooks, market.celer]);

    const [askHighlights, setAskHighlights] = useState<GetColorIndexesReturn>();
    const [bidHighlights, setBidHighlights] = useState<GetColorIndexesReturn>();
    const [askVWAPHighlights, setAskVWAPHighlights] = useState<GetColorIndexesReturn>();
    const [bidVWAPHighlights, setBidVWAPHighlights] = useState<GetColorIndexesReturn>();

    const { formatPip, formatHighlights, formatSpread } = useInstrument(market.celer);

    // currently displaying bids and asks
    const spread = useMemo(() => {
        if (best.bid && prevBest.bid) return formatSpread(BigNumber(best.ask).minus(best.bid));
        else return undefined;
    }, [best.bid, best.ask]);

    const vwap = useMemo(() => {
        if (quantity) {
            const reversedAsks = asks ? [...asks].reverse() : [];
            if (duration !== TimeInForceType.FOK) {
                const ask = calculateTickerVWAP(reversedAsks, quantity);
                const bid = calculateTickerVWAP(bids, quantity);

                const bidVWAP = bid.vwap ? bid.vwap.decimalPlaces(priceDecimals, BigNumber.ROUND_FLOOR).toNumber() : 0;
                const askVWAP = ask.vwap ? ask.vwap.decimalPlaces(priceDecimals, BigNumber.ROUND_CEIL).toNumber() : 0;

                return {
                    bid: bidVWAP,
                    ask: askVWAP,
                    isNoBidPrice: !bid.vwap,
                    isNoAskPrice: !ask.vwap
                };
            } else {
                // For FOK, we only consider layers that have quantity available
                const availableAsks = reversedAsks.filter((ask) => ask.quantity >= quantity);
                const availableBids = bids?.filter((bid) => bid.quantity >= quantity) || [];

                const ask = availableAsks.length > 0 ? availableAsks[0] : null;
                const bid = availableBids.length > 0 ? availableBids[0] : null;

                return {
                    bid: bid?.price || 0,
                    ask: ask?.price || 0,
                    isNoBidPrice: !bid,
                    isNoAskPrice: !ask
                };
            }
        } else {
            return { bid: 0, ask: 0, isNoBidPrice: false, isNoAskPrice: false };
        }
    }, [bids, asks, priceDecimals, quantity, duration]);

    const vwapSpread = useMemo(() => {
        if (vwap.bid && vwap.ask) return formatSpread(BigNumber(vwap.ask).minus(vwap.bid));
        else return undefined;
    }, [vwap.bid, vwap.ask]);

    const bestBid = useMemo(
        () => ({
            price: best.bid,
            pricePips: best.bid ? formatPip(best.bid) : undefined,
            highlights: bidHighlights
        }),
        [best.bid]
    );

    const bestAsk = useMemo(
        () => ({
            price: best.ask,
            pricePips: best.ask ? formatPip(best.ask) : undefined,
            highlights: askHighlights
        }),
        [best.ask]
    );

    const bidVWAP = useMemo(
        () => ({
            price: vwap.bid,
            pricePips: vwap.bid ? formatPip(vwap.bid) : undefined,
            highlights: bidVWAPHighlights,
            isNoPrice: vwap.isNoBidPrice || false
        }),
        [vwap.bid]
    );

    const askVWAP = useMemo(
        () => ({
            price: vwap.ask,
            pricePips: vwap.ask ? formatPip(vwap.ask) : undefined,
            highlights: askVWAPHighlights,
            isNoPrice: vwap.isNoAskPrice || false
        }),
        [vwap.ask]
    );

    const prevVWAP = usePreviousState(vwap) as { bid: number; ask: number };

    // for highlighting changes based on prev price
    useDidUpdate(() => {
        if (best.bid && prevBest.bid) setBidHighlights(formatHighlights(prevBest.bid, best.bid, bidHighlights));
    }, [best.bid]);

    useDidUpdate(() => {
        if (best.ask && prevBest.ask) setAskHighlights(formatHighlights(prevBest.ask, best.ask, askHighlights));
    }, [best.ask]);

    useDidUpdate(() => {
        if (vwap.bid && prevVWAP.bid) setBidVWAPHighlights(formatHighlights(prevVWAP.bid, vwap.bid, bidVWAPHighlights));
    }, [vwap.bid]);

    useDidUpdate(() => {
        if (vwap.ask && prevVWAP.ask) setAskVWAPHighlights(formatHighlights(prevVWAP.ask, vwap.ask, askVWAPHighlights));
    }, [vwap.ask]);

    return {
        bestBid,
        bestAsk,
        bidVWAP,
        askVWAP,
        spread,
        vwapSpread,
        settlementDate: best.settlementDate
    };
};

export default useTickerOrderBook;
